/**
 * A phone as a form deals with it: one stored string, shown as a calling code and a national part.
 *
 * Nothing here guesses a calling code. A number that arrives without one keeps its digits and gets
 * none, so the selector stands empty and the merchant picks it: the address country is not the
 * number's country, and lending its code would put a prefix on a number nobody checked.
 *
 * The country and the calling code are kept apart on purpose: a toll-free +800 or a satellite +870
 * number carries a code and belongs to no country at all.
 */
import { makeAutoObservable } from "mobx";
import {
    CountryCode,
    getCountryCallingCode,
    isSupportedCountry,
    parsePhoneNumberFromString,
} from "libphonenumber-js/max";

/** What the field can say is wrong with a number; the wording for each lives with the field. */
export const PHONE_ERROR = {
    MISSING_CALLING_CODE: "missing-calling-code",
    INVALID_NUMBER: "invalid-number",
} as const;

export type PhoneError = typeof PHONE_ERROR[keyof typeof PHONE_ERROR];

class PhoneValue {
    private _country: CountryCode | undefined;
    private _callingCode: string | undefined;
    /** Digits shown in the number field; raw digits when the number could not be read. */
    private _nationalNumber = "";
    /** E.164 as the library writes it, empty while the pair is not a number it confirms. */
    private _value = "";

    constructor(value?: string) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._setFromWrittenNumber((value ?? "").trim());
    }

    /**
     * The code a country dials, for callers holding a country and no number yet - the list the
     * selector is filled from. Undefined where there is nothing to dial.
     */
    public static callingCodeOf(country: string | undefined): string | undefined {
        const dialable = PhoneValue._dialableCountry(country);

        if (!dialable) {
            return undefined;
        }

        return getCountryCallingCode(dialable);
    }

    public get country(): CountryCode | undefined { return this._country; }

    public get callingCode(): string | undefined { return this._callingCode; }

    public get nationalNumber(): string { return this._nationalNumber; }

    /**
     * The single string the store and the API keep. Only a number the library confirmed is written
     * out at all - anything half-formed is empty rather than plausible, so it cannot reach a
     * carrier even if something upstream forgets to ask isValid.
     */
    public get value(): string { return this._value; }

    /**
     * What is wrong with the number, so the field says it rather than working it out again from the
     * parts. Emptiness is not an error here: required decides whether a phone is needed at all.
     */
    public get error(): PhoneError | undefined {
        if (!this._nationalNumber || this._value) {
            return undefined;
        }

        if (!this._callingCode) {
            return PHONE_ERROR.MISSING_CALLING_CODE;
        }

        return PHONE_ERROR.INVALID_NUMBER;
    }

    public get isValid(): boolean { return !this.error; }

    /** The digits are read again against the country picked: they were written for it. */
    public setCountry(country: string | undefined): void {
        this._setCountry(country);
        this._setFromWrittenNumber(this._nationalNumber);
    }

    public setNumber(input: string): void {
        this._setFromWrittenNumber(input);
    }

    /** A country that cannot be dialled leaves both empty rather than half-set. */
    private _setCountry(country: string | undefined): void {
        this._country = PhoneValue._dialableCountry(country);
        this._callingCode = PhoneValue.callingCodeOf(this._country);
    }

    /**
     * Sets the country, the calling code and the digits from a number as it is written. One that
     * states its own code is split on it even when it is not a whole number yet - "+43678" says
     * +43 itself, which is a fact and not a guess. Bare digits are split only once the library
     * confirms them: until then where the trunk zero ends is a guess, and it would jump under the
     * merchant's fingers mid-number. When nothing can be split off, only the digits are set and
     * whatever code the field carries stays.
     */
    private _setFromWrittenNumber(text: string): void {
        const statesItsOwnCode = text.trim().startsWith("+");
        const parsed = parsePhoneNumberFromString(text, this._country);

        if (parsed && (statesItsOwnCode || parsed.isValid())) {
            this._country = parsed.country;
            this._callingCode = parsed.countryCallingCode;
            this._nationalNumber = parsed.nationalNumber;
        } else {
            this._nationalNumber = PhoneValue._toDigits(text);
        }

        this._value = this._confirmedE164();
    }

    /**
     * What the two fields dial, but only when the library confirms it is a number at all. Read
     * here rather than from the parse above: a toll-free +800 or a satellite +870 belongs to no
     * country, so its digits alone parse as nothing and only the pair comes out a real number.
     */
    private _confirmedE164(): string {
        if (!this._callingCode || !this._nationalNumber) {
            return "";
        }

        const parsed = parsePhoneNumberFromString(`+${this._callingCode}${this._nationalNumber}`);

        if (!parsed?.isValid()) {
            return "";
        }

        return parsed.number;
    }

    /** Undefined for the seven countries that cannot be dialled at all - Antarctica, Bouvet Island, ... */
    private static _dialableCountry(country: string | undefined): CountryCode | undefined {
        if (!country || !isSupportedCountry(country)) {
            return undefined;
        }

        return country;
    }

    private static _toDigits(value: string): string {
        return value.replace(/\D/g, "");
    }
}

export default PhoneValue;
